import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { isSystemOrg } from 'app/core/constants/system-org.constants';
import { requireAuthSession } from 'app/core/guards/require-auth-session';
import { hasAnyRole, ROLE_GROUPS } from 'app/shared/constants';
import { of } from 'rxjs';

/**
 * #61 security fix — `/billing` used to be gated on role alone
 * (`PHOTOGRAPHER_AND_ABOVE`), which was widened specifically so a SOLO
 * SystemOrg photographer's own Stripe checkout return (`/billing/checkout/
 * {success,cancel}`) never 403s. That same PHOTOGRAPHER role also belongs to
 * a studio employee, so the widened gate let a studio photographer see (and,
 * via the be-app endpoints, act on) the STUDIO's shared billing subject too.
 *
 * Same "org owner tier OR personal SystemOrg" shape the old marketplace
 * provider guard used (kb#2709 made marketplace personal-only since), with
 * `ROLE_GROUPS.ORG_OWNERS` (ORG_OWNER + platform staff) — WS-B §7.2 is
 * explicit that a studio's billing/payment/orders are ORG_OWNER-only.
 *
 * A SystemOrg caller holding PHOTOGRAPHER or USER is allowed: the billing
 * subject there is always the caller themself (their own plan / Stripe
 * checkout return / CUSTOMER_A's own subscription), never another user's.
 * Other SystemOrg roles (EDITOR) are refused to match be-app `Payment.Read`
 * (kb#5791) — otherwise the page opens and every API call under it 403s.
 */
export const BillingAccessGuard: CanActivateFn = (_route, state) => {
    const router = inject(Router);
    const returnUrl = state.url || '/signed-in-redirect';

    return requireAuthSession(returnUrl, (auth) => {
        const orgId = auth.user.organizationId || auth.user.org_id;
        const allowed =
            hasAnyRole(auth.user.roles, [...ROLE_GROUPS.ORG_OWNERS]) ||
            (isSystemOrg(orgId) &&
                hasAnyRole(auth.user.roles, [...ROLE_GROUPS.PERSONAL_BILLING]));

        if (allowed) {
            return of(true);
        }

        console.warn(
            '[BillingAccessGuard] Not a studio ORG_OWNER/platform staff and not a SystemOrg PHOTOGRAPHER/USER. Redirecting to forbidden.'
        );
        // #82: this guard runs post-AuthGuard, inside the shell — same fail-closed
        // contract as RouteDataAuthorizationGuard, so the target must stay in-shell
        // too instead of dropping the user onto the bare `/errors/forbidden` page.
        return of(router.createUrlTree(['/forbidden']));
    });
};
